import type { MessageKey } from "@erp/i18n";
import { SALES_CHANNEL_VALUES } from "@erp/shared";
import { formatMonth } from "@erp/i18n/format";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, type LinkProps } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Flame,
  LifeBuoy,
  PackageX,
  ShoppingCart,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "~/components/ui/badge";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { ChannelBar } from "~/features/reports/channel-bar";
import { MonthlyChart } from "~/features/reports/report-charts";
import { DashboardBoard, type WidgetDefinition } from "~/features/dashboard/board";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { useFormat, useLocale, useT } from "~/lib/i18n";
import { useCan, useSession } from "~/lib/session";

export const Route = createFileRoute("/_app/")({ component: Dashboard });

const fetchDashboard = () => unwrap(api.reports.dashboard.get());

interface Alert {
  key: string;
  count: number;
  message: MessageKey;
  icon: LucideIcon;
  tone: "danger" | "warning" | "neutral";
  link: LinkProps;
  /** Extra values of the message (a configurable delay, for instance). */
  values?: Record<string, number>;
}

function Dashboard() {
  const t = useT();
  const user = useSession();
  const can = useCan();
  const greeting = t("dashboard.greeting", { name: user.name.split(" ")[0] ?? user.name });
  return can("reports:read") ? <Management greeting={greeting} /> : <Operations greeting={greeting} />;
}

function Card({
  title,
  children,
  className,
  action,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <Panel className={className}>
      <div className="flex items-center justify-between gap-3 px-4 pt-4 md:px-5">
        <h2 className="text-[15px] font-medium">{title}</h2>
        {action}
      </div>
      <div className="px-4 pt-3 pb-4 md:px-5 md:pb-5">{children}</div>
    </Panel>
  );
}

function AlertList({ alerts }: { alerts: Alert[] }) {
  const t = useT();
  const active = alerts.filter((a) => a.count > 0);
  if (active.length === 0)
    return (
      <p className="flex items-center gap-2 py-2 text-sm text-fg-2">
        <CheckCircle2 className="size-4 text-success" aria-hidden />
        {t("dashboard.alerts.allClear")}
      </p>
    );
  return (
    <ul className="-mx-2 flex flex-col">
      {active.map((a) => (
        <li key={a.key}>
          <Link {...a.link} className="flex min-h-11 items-center gap-3 rounded-control px-2 text-sm hover:bg-surface-2">
            <a.icon
              className={cn(
                "size-4 shrink-0",
                a.tone === "danger" ? "text-danger" : a.tone === "warning" ? "text-warning" : "text-fg-3",
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1">{t(a.message, { count: a.count, ...a.values })}</span>
            <ChevronRight className="size-4 shrink-0 text-fg-3" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Revenue, margin and margin rate of a period; the margin only covers revenue whose cost is known. */
function PeriodCard({
  title,
  figures,
}: {
  title: string;
  figures: { netCents: number; costedNetCents: number; marginCents: number };
}) {
  const t = useT();
  const format = useFormat();
  const rate = figures.costedNetCents > 0 ? figures.marginCents / figures.costedNetCents : null;
  const uncosted = figures.netCents - figures.costedNetCents;
  return (
    <Panel className="px-5 py-4 md:px-6 md:py-5">
      <h2 className="text-[13px] font-medium text-fg-2">{title}</h2>
      <p className="mt-1 font-display text-3xl font-medium tabular md:text-[34px]">{format.money(figures.netCents)}</p>
      <p className="text-xs text-fg-3">{t("dashboard.period.revenue")}</p>
      <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-line pt-3 text-sm">
        <div>
          <dt className="text-xs text-fg-3">{t("dashboard.period.margin")}</dt>
          <dd className="mt-0.5 font-medium tabular">{format.money(figures.marginCents)}</dd>
        </div>
        <div>
          <dt className="text-xs text-fg-3">{t("dashboard.period.rate")}</dt>
          <dd className="mt-0.5 font-medium tabular">{rate === null ? "—" : format.percent(rate)}</dd>
        </div>
      </dl>
      {uncosted > 0 ? (
        <p className="mt-2 text-xs text-fg-3">{t("dashboard.period.uncosted", { amount: format.money(uncosted) })}</p>
      ) : null}
    </Panel>
  );
}

/** Revenue of a period split by sales channel (web, quotes, IABAKO history…), each with its share. */
function ChannelCard({ title, byChannel }: { title: string; byChannel: Partial<Record<string, number>> }) {
  const t = useT();
  const slices = SALES_CHANNEL_VALUES.filter((c) => (byChannel[c] ?? 0) !== 0).map((c) => ({
    key: c,
    label: t(`reports.channels.${c}`),
    cents: byChannel[c] ?? 0,
    color: `var(--chart-${c})`,
  }));
  return (
    <Card title={title}>
      {slices.length ? <ChannelBar slices={slices} /> : <p className="text-sm text-fg-3">{t("dashboard.channelsEmpty")}</p>}
    </Card>
  );
}

function CountLink({ count, label, link, tone }: { count: number; label: string; link: LinkProps; tone?: "danger" | "warning" }) {
  const format = useFormat();
  return (
    <Link {...link} className="flex min-h-11 items-center gap-3 rounded-control px-2 text-sm hover:bg-surface-2">
      <span
        className={cn(
          "min-w-9 font-display text-xl font-medium tabular",
          count > 0 && tone === "danger" ? "text-danger" : count > 0 && tone === "warning" ? "text-warning" : "text-fg",
        )}
      >
        {format.number(count)}
      </span>
      <span className="min-w-0 flex-1 text-fg-2">{label}</span>
      <ChevronRight className="size-4 shrink-0 text-fg-3" aria-hidden />
    </Link>
  );
}

function Management({ greeting }: { greeting: string }) {
  const t = useT();
  const format = useFormat();
  const [locale] = useLocale();
  const query = useQuery({ queryKey: ["reports", "dashboard"], queryFn: fetchDashboard, refetchInterval: 5 * 60_000 });

  if (query.isError)
    return (
      <Page>
        <PageHeader title={greeting} />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  if (query.isPending)
    return (
      <Page>
        <PageHeader title={greeting} />
        <div className="grid gap-5 md:grid-cols-2">
          {Array.from({ length: 2 }, (_, i) => (
            <Panel key={i} className="px-5 py-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-2 h-9 w-40" />
              <Skeleton className="mt-5 h-10 w-full" />
            </Panel>
          ))}
        </div>
        <Skeleton className="mt-5 h-72 w-full" />
      </Page>
    );

  const d = query.data;
  const monthName = formatMonth(d.series.at(-1)?.month ?? "", locale, "long");
  const definitions: WidgetDefinition[] = [
    {
      type: "month",
      title: t("dashboard.period.month", { month: monthName }),
      description: t("dashboard.widgets.defs.month"),
      render: () => <PeriodCard title={t("dashboard.period.month", { month: monthName })} figures={d.month.current} />,
      layout: { x: 0, y: 0, w: 6, h: 4 },
    },
    {
      type: "year",
      title: t("dashboard.period.year", { year: d.year }),
      description: t("dashboard.widgets.defs.year"),
      render: () => <PeriodCard title={t("dashboard.period.year", { year: d.year })} figures={d.ytd.current} />,
      layout: { x: 6, y: 0, w: 6, h: 4 },
    },
    {
      type: "monthly",
      title: t("dashboard.monthly.title", { year: d.year }),
      description: t("dashboard.widgets.defs.monthly"),
      render: () => (
        <Card title={t("dashboard.monthly.title", { year: d.year })}>
          <MonthlyChart
            rows={d.series.map((m) => ({
              label: m.month,
              net: m.netCents,
              margin: m.marginCents,
              rate: m.costedNetCents > 0 ? m.marginCents / m.costedNetCents : 0,
            }))}
            series={[
              { key: "net", label: t("dashboard.period.revenue"), color: "var(--chart-current)", kind: "bar" },
              { key: "margin", label: t("dashboard.period.margin"), color: "var(--chart-margin)", kind: "bar" },
              { key: "rate", label: t("dashboard.period.rate"), color: "var(--chart-quote)", kind: "line", ratio: true },
            ]}
          />
        </Card>
      ),
      layout: { x: 0, y: 4, w: 12, h: 8 },
      minW: 6,
      minH: 6,
    },
    {
      type: "channelsMonth",
      title: t("dashboard.channelsTitle"),
      description: t("dashboard.widgets.defs.channelsMonth"),
      render: () => <ChannelCard title={t("dashboard.channelsTitle")} byChannel={d.month.current.byChannel} />,
      layout: { x: 0, y: 12, w: 6, h: 3 },
    },
    {
      type: "channelsYear",
      title: t("dashboard.channelsTitleYear", { year: d.year }),
      description: t("dashboard.widgets.defs.channelsYear"),
      render: () => (
        <ChannelCard title={t("dashboard.channelsTitleYear", { year: d.year })} byChannel={d.ytd.current.byChannel} />
      ),
      layout: { x: 6, y: 12, w: 6, h: 3 },
    },
    {
      type: "orders",
      title: t("dashboard.orders.title"),
      description: t("dashboard.widgets.defs.orders"),
      render: () => (
        <Card title={t("dashboard.orders.title")}>
          <div className="-mx-2 flex flex-col">
            <CountLink count={d.orders.open} label={t("dashboard.orders.open")} link={{ to: "/orders" }} />
            <CountLink
              count={d.orders.urgent}
              label={t("dashboard.orders.urgent")}
              link={{ to: "/orders", search: { flag: "urgent" } }}
              tone="danger"
            />
            <CountLink
              count={d.orders.ready}
              label={t("dashboard.orders.ready")}
              link={{ to: "/orders", search: { flag: "ready" } }}
            />
            <CountLink
              count={d.orders.stockout}
              label={t("dashboard.orders.stockout")}
              link={{ to: "/orders", search: { flag: "stockout" } }}
              tone="warning"
            />
            <CountLink
              count={d.orders.remainder}
              label={t("dashboard.orders.remainder")}
              link={{ to: "/orders", search: { flag: "remainder" } }}
            />
          </div>
        </Card>
      ),
      layout: { x: 0, y: 15, w: 6, h: 6 },
    },
    {
      type: "stock",
      title: t("dashboard.stock.title"),
      description: t("dashboard.widgets.defs.stock"),
      render: () => (
        <Card title={t("dashboard.stock.title")}>
          <div className="-mx-2 flex flex-col">
            <CountLink
              count={d.stock.outOfStock}
              label={t("dashboard.stock.outOfStock")}
              link={{ to: "/stock", search: { level: "out" } }}
              tone="danger"
            />
            <CountLink
              count={d.stock.lowStock}
              label={t("dashboard.stock.lowStock")}
              link={{ to: "/stock", search: { level: "low" } }}
              tone="warning"
            />
          </div>
          <Link
            to="/stock/valuation"
            className="mt-2 flex items-baseline justify-between gap-3 border-t border-line pt-3 text-sm hover:underline"
          >
            <span className="text-fg-2">{t("dashboard.stock.value")}</span>
            <span className="font-medium tabular">{format.money(d.stock.purchaseValueCents)}</span>
          </Link>
        </Card>
      ),
      layout: { x: 6, y: 15, w: 6, h: 6 },
    },
    {
      type: "receivables",
      title: t("dashboard.receivables.title"),
      description: t("dashboard.widgets.defs.receivables"),
      render: () => <Receivables data={d.receivables} />,
      layout: { x: 0, y: 21, w: 12, h: 10 },
    },
  ];
  return (
    <Page>
      <PageHeader title={greeting} meta={t("dashboard.asOf", { date: format.date(d.asOf, "long") })} />
      <DashboardBoard definitions={definitions} />
    </Page>
  );
}

/** « Factures à encaisser » (call of 04/11): what customers still owe, the oldest due date first. */
function Receivables({ data }: { data: Awaited<ReturnType<typeof fetchDashboard>>["receivables"] }) {
  const t = useT();
  const format = useFormat();
  return (
    <Card title={t("dashboard.receivables.title")}>
      {data.count === 0 ? (
        <p className="text-sm text-fg-3">{t("dashboard.receivables.empty")}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <p className="text-sm text-fg-2">
              <span className="mr-2 font-display text-2xl font-medium text-fg tabular">{format.money(data.totalCents)}</span>
              {t("dashboard.receivables.total", { count: data.count })}
            </p>
            {data.overdueCount > 0 ? (
              <p className="text-sm text-danger">
                <span className="mr-1.5 font-medium tabular">{format.money(data.overdueCents)}</span>
                {t("dashboard.receivables.overdue")}
              </p>
            ) : null}
          </div>
          <ul className="-mx-2 mt-3 flex flex-col">
            {data.items.map((r) => (
              <li key={r.id}>
                <Link
                  to="/invoices/$id"
                  params={{ id: r.id }}
                  className="flex min-h-11 items-center gap-3 rounded-control px-2 text-sm hover:bg-surface-2"
                >
                  <span className="w-32 shrink-0 font-medium whitespace-nowrap tabular">{r.number}</span>
                  <span className="min-w-0 flex-1 truncate text-fg-2">{r.customerName}</span>
                  <span className={cn("shrink-0 whitespace-nowrap max-sm:hidden", r.overdue ? "text-danger" : "text-fg-3")}>
                    {r.dueDate
                      ? t("dashboard.receivables.due", { date: format.date(r.dueDate, "short") })
                      : t("dashboard.receivables.noDue")}
                  </span>
                  {r.overdue ? <Badge tone="danger">{t("dashboard.receivables.late")}</Badge> : null}
                  <span className="w-28 shrink-0 text-right font-medium tabular">{format.money(r.balanceCents)}</span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/invoices"
            search={{ filter: "unpaid" }}
            className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-3 text-sm text-fg-2 hover:text-fg"
          >
            {t("dashboard.receivables.all")}
            <ChevronRight className="size-4" aria-hidden />
          </Link>
        </>
      )}
    </Card>
  );
}

/** Roles without access to financial figures get the operational to-do list. */
function Operations({ greeting }: { greeting: string }) {
  const t = useT();
  const can = useCan();
  const [orders, stock, sav] = useQueries({
    queries: [
      {
        queryKey: ["orders", "counts"],
        queryFn: () => unwrap(api.orders.get({ query: { view: "open", offset: 0, limit: 1 } })),
        enabled: can("sales:read"),
      },
      {
        queryKey: ["stock", "counts"],
        queryFn: () => unwrap(api.stock.get({ query: { offset: 0, limit: 1, archived: "exclude", dir: "asc" } })),
        enabled: can("stock:read"),
      },
      {
        queryKey: ["sav", "active-count"],
        queryFn: () => unwrap(api.sav.get({ query: { status: "active", offset: 0, limit: 1 } })),
        enabled: can("sav:read"),
      },
    ],
  });
  const loading = [orders, stock, sav].some((q) => q.isLoading);
  const counts = orders.data?.counts ?? {};
  const alerts: Alert[] = [
    {
      key: "open",
      count: counts.open ?? 0,
      message: "dashboard.alerts.openOrders",
      icon: ShoppingCart,
      tone: "neutral",
      link: { to: "/orders" },
    },
    {
      key: "urgent",
      count: counts.urgent ?? 0,
      message: "dashboard.alerts.urgentOrders",
      icon: Flame,
      tone: "danger",
      link: { to: "/orders", search: { flag: "urgent" } },
    },
    {
      key: "stockout",
      count: counts.stockout ?? 0,
      message: "dashboard.alerts.stockoutOrders",
      icon: AlertTriangle,
      tone: "warning",
      link: { to: "/orders", search: { flag: "stockout" } },
    },
    {
      key: "out",
      count: stock.data?.counts.out ?? 0,
      message: "dashboard.alerts.outOfStock",
      icon: PackageX,
      tone: "danger",
      link: { to: "/stock", search: { level: "out" } },
    },
    {
      key: "low",
      count: stock.data?.counts.low ?? 0,
      message: "dashboard.alerts.lowStock",
      icon: PackageX,
      tone: "warning",
      link: { to: "/stock", search: { level: "low" } },
    },
    {
      key: "sav",
      count: sav.data?.total ?? 0,
      message: "dashboard.alerts.activeSav",
      icon: LifeBuoy,
      tone: "neutral",
      link: { to: "/sav" },
    },
  ];
  return (
    <Page>
      <PageHeader title={greeting} meta={t("dashboard.opsMeta")} />
      <Card title={t("dashboard.alerts.title")} className="max-w-2xl">
        {loading ? <Skeleton className="h-40 w-full" /> : <AlertList alerts={alerts} />}
      </Card>
    </Page>
  );
}
